<Container
  x={blockPosition().x}
  y={blockPosition().y}
  width={blockSize().width}
  height={blockSize().height}
  minWidth={blockSize().width}
  minHeight={blockSize().height}
  display="flex"
  flexDirection="column"
  justifyContent="center"
  alignItems="center"
  rowGap={gap().row}
>
  @for (row of renderedRows) {
    <Container
      width="100%"
      height={row.height}
      display="flex"
      flexDirection="row"
      justifyContent="center"
      alignItems="center"
      columnGap={gap().column}
    >
      @for (entry of row.entries) {
        <Container
          width={entry.width}
          height={entry.height}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <entry.component object={sprite} ...entry.props />
        </Container>
      }
    </Container>
  }
</Container>

<script>
import { computed } from "canvasengine";
import { RpgClientEngine } from "../RpgClientEngine";
import { inject } from "../core/inject";
import { resolveDynamicProps, resolveDynamicValue } from "./dynamics/parse-value";
import {
  computeBlockPosition,
  computeBlockSize,
  estimateComponentSize,
  getComponentId,
  getComponentProps
} from "./player-components-utils";

const { object, position, graphicBounds } = defineProps({
  position: {
    default: 'top'
  }
});
const sprite = object();

const client = inject(RpgClientEngine);
const warnedComponents = new Set();

const readPosition = computed(() => position?.() ?? 'top');

const componentSource = computed(() => {
  switch (readPosition()) {
    case 'bottom':
      return sprite.componentsBottom?.();
    case 'center':
      return sprite.componentsCenter?.();
    case 'left':
      return sprite.componentsLeft?.();
    case 'right':
      return sprite.componentsRight?.();
    case 'top':
    default:
      return sprite.componentsTop?.();
  }
});

const componentData = computed(() => {
  const value = componentSource();
  if (!value) {
    return { components: [], layout: {} };
  }

  if (typeof value !== 'string') {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    if (typeof process === 'undefined' || process.env?.NODE_ENV !== 'production') {
      console.warn('[RPGJS] Invalid server sprite component payload', error);
    }
    return { components: [], layout: {} };
  }
});

const layout = computed(() => componentData()?.layout ?? {});
const rows = computed(() => componentData()?.components ?? []);
const hitbox = sprite.hitbox;

const toNumber = (value, fallback = 0) => {
  const resolved = resolveDynamicValue(value, sprite);
  const num = typeof resolved === 'number' ? resolved : parseFloat(resolved);
  return Number.isFinite(num) ? num : fallback;
};

const estimateTextWidth = (value, style = {}) => {
  const text = String(resolveDynamicValue(value ?? '', sprite) ?? '');
  const fontSize = toNumber(style.fontSize, 12);
  return Math.max(1, Math.ceil(text.length * fontSize * 0.6));
};

const rowMetrics = computed(() => {
  return rows().map((row) => {
    const cells = row.map((definition) => {
      const intrinsic = estimateComponentSize(definition, { toNumber, estimateTextWidth });
      return {
        definition,
        width: intrinsic.width,
        height: intrinsic.height
      };
    });

    return {
      cells,
      width: cells.reduce((sum, cell) => sum + cell.width, 0),
      height: cells.reduce((max, cell) => Math.max(max, cell.height), 0)
    };
  });
});

const gap = computed(() => ({
  row: toNumber(layout().rowGap ?? layout().gap, 0),
  column: toNumber(layout().columnGap ?? layout().gap, 0)
}));

const fallbackBounds = computed(() => {
  const box = hitbox();
  const width = box?.w ?? 0;
  const height = box?.h ?? 0;

  return {
    left: 0,
    top: 0,
    right: width,
    bottom: height,
    width,
    height,
    centerX: width / 2,
    centerY: height / 2
  };
});

const bounds = computed(() => {
  const resolvedBounds = typeof graphicBounds === 'function' ? graphicBounds() : undefined;
  return resolvedBounds ?? fallbackBounds();
});

const blockSize = computed(() => {
  const box = hitbox() ?? { w: 0, h: 0 };
  const graphic = bounds();
  return computeBlockSize({
    position: readPosition(),
    layout: layout(),
    rowMetrics: rowMetrics(),
    gap: gap(),
    graphic,
    hitbox: box
  });
});

const blockPosition = computed(() => {
  const box = hitbox() ?? { w: 0, h: 0 };
  const graphic = bounds();
  return computeBlockPosition({
    position: readPosition(),
    size: blockSize(),
    layout: layout(),
    graphic,
    hitbox: box
  });
});

const warnMissingComponent = (id) => {
  if (!id || warnedComponents.has(id)) return;
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'production') return;
  warnedComponents.add(id);
  console.warn(`[RPGJS] Server sprite component "${id}" is not registered on the client.`);
};

const renderedRows = computed(() => {
  return rowMetrics().map((row) => {
    const entries = [];

    row.cells.forEach((cell) => {
      const definition = cell.definition;
      const id = getComponentId(definition);
      const component = client.getSpriteComponent(id);

      if (!component) {
        warnMissingComponent(id);
        return;
      }

      entries.push({
        component,
        props: resolveDynamicProps(getComponentProps(definition), sprite),
        width: cell.width,
        height: cell.height
      });
    });

    return {
      height: row.height,
      entries
    };
  });
});
</script>
